import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SearchOutlined,
  BookOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  CloseOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { Button, Input, Slider, Tooltip, message, Spin } from 'antd';
import './index.scss';

// Dynamically load PDF.js from CDN to avoid bundle configuration errors
const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

const BookReader = ({ pdfUrl, bookTitle }) => {
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // Left page index (even) in dual-view, or single page index
  const [pageImages, setPageImages] = useState({}); // Stores data URLs: { pageNum: dataUrl }
  const [renderingPages, setRenderingPages] = useState({}); // Track pages currently rendering
  const [isLoading, setIsLoading] = useState(true);

  // Customization controls
  const [scale, setScale] = useState(1.2);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('dual'); // 'dual' or 'single'
  const [isMagnifierEnabled, setIsMagnifierEnabled] = useState(true);

  // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('thumbnails'); // 'thumbnails' or 'search'

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Flip states
  const [flipDirection, setFlipDirection] = useState(null); // 'next' or 'prev'
  const [flipActive, setFlipActive] = useState(false);

  const containerRef = useRef(null);
  const audioContextRef = useRef(null);

  // Initialize PDF document
  useEffect(() => {
    let active = true;
    setIsLoading(true);

    loadPdfJs()
      .then((pdfjs) => {
        if (!active) return;
        return pdfjs.getDocument(pdfUrl).promise;
      })
      .then((pdfDoc) => {
        if (!active || !pdfDoc) return;
        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error loading PDF:', err);
        message.error('Failed to load the book PDF file.');
        setIsLoading(false);
      });

    // Detect screen width for responsive view mode
    const handleResize = () => {
      if (window.innerWidth < 992) {
        setViewMode('single');
      } else {
        setViewMode('dual');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      active = false;
      window.removeEventListener('resize', handleResize);
    };
  }, [pdfUrl]);

  // Synchronize fullscreen state from document listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Web Audio page rustle sound synthesizer
  const playFlipSound = () => {
    if (isMuted) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioCtx = audioContextRef.current;
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }

      const duration = 0.45;
      const bufferSize = audioCtx.sampleRate * duration;
      const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
      const data = buffer.getChannelData(0);

      // Generate noise with exponential decay
      for (let i = 0; i < bufferSize; i++) {
        const t = i / audioCtx.sampleRate;
        const decay = Math.exp(-6 * t);
        // Soft white noise + low frequency rustle
        data[i] = (Math.random() * 2 - 1) * 0.15 * decay;
      }

      const noise = audioCtx.createBufferSource();
      noise.buffer = buffer;

      // Bandpass filter to model paper rubbing frequency
      const filter = audioCtx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(600, audioCtx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(1800, audioCtx.currentTime + duration);
      filter.Q.value = 1.8;

      // Gain envelope
      const gainNode = audioCtx.createGain();
      gainNode.gain.setValueAtTime(0.08, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);

      noise.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      noise.start();
    } catch (e) {
      console.warn('Audio synthesis failed:', e);
    }
  };

  // Render PDF page onto temporary canvas and extract as Data URL
  const fetchPageImage = async (pageNum) => {
    if (!pdf || pageNum < 1 || pageNum > numPages) return;
    if (pageImages[pageNum] || renderingPages[pageNum]) return;

    setRenderingPages(prev => ({ ...prev, [pageNum]: true }));

    try {
      const page = await pdf.getPage(pageNum);
      const tempViewport = page.getViewport({ scale: 1.5 }); // High DPI rendering

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = tempViewport.width;
      canvas.height = tempViewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: tempViewport,
      };

      await page.render(renderContext).promise;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

      setPageImages(prev => ({ ...prev, [pageNum]: dataUrl }));
    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error);
    } finally {
      setRenderingPages(prev => ({ ...prev, [pageNum]: false }));
    }
  };

  // Pre-load neighboring pages for zero-latency scrolling
  const preloadPages = (centerPage) => {
    const pagesToLoad = [];
    if (viewMode === 'dual') {
      const start = Math.max(1, centerPage - 2);
      const end = Math.min(numPages, centerPage + 4);
      for (let p = start; p <= end; p++) {
        pagesToLoad.push(p);
      }
    } else {
      const start = Math.max(1, centerPage - 1);
      const end = Math.min(numPages, centerPage + 2);
      for (let p = start; p <= end; p++) {
        pagesToLoad.push(p);
      }
    }

    pagesToLoad.forEach(p => {
      if (!pageImages[p] && !renderingPages[p]) {
        fetchPageImage(p);
      }
    });
  };

  // Pre-load on currentPage change
  useEffect(() => {
    if (pdf) {
      preloadPages(currentPage);
    }
  }, [currentPage, pdf, viewMode]);

  // Determine page layouts for current view
  const visiblePages = useMemo(() => {
    if (viewMode === 'single') {
      return { left: null, right: currentPage };
    } else {
      if (currentPage === 1) {
        return { left: null, right: 1 }; // Cover page on right side
      }
      const left = currentPage;
      const right = currentPage + 1 <= numPages ? currentPage + 1 : null;
      return { left, right };
    }
  }, [currentPage, viewMode, numPages]);

  // Page turning handlers
  const handleNext = () => {
    if (flipActive || isLoading) return;

    if (viewMode === 'single') {
      if (currentPage < numPages) {
        setFlipDirection('next');
        setFlipActive(true);
        playFlipSound();

        setTimeout(() => {
          setCurrentPage(prev => prev + 1);
          setFlipActive(false);
          setFlipDirection(null);
        }, 500);
      }
    } else {
      // Dual view mode navigation
      const nextLeft = currentPage === 1 ? 2 : currentPage + 2;
      if (nextLeft <= numPages) {
        setFlipDirection('next');
        setFlipActive(true);
        playFlipSound();

        // Make sure next pages are pre-loaded
        fetchPageImage(nextLeft);
        if (nextLeft + 1 <= numPages) fetchPageImage(nextLeft + 1);

        setTimeout(() => {
          setCurrentPage(nextLeft);
          setFlipActive(false);
          setFlipDirection(null);
        }, 500);
      }
    }
  };

  const handlePrev = () => {
    if (flipActive || isLoading) return;

    if (viewMode === 'single') {
      if (currentPage > 1) {
        setFlipDirection('prev');
        setFlipActive(true);
        playFlipSound();

        setTimeout(() => {
          setCurrentPage(prev => prev - 1);
          setFlipActive(false);
          setFlipDirection(null);
        }, 500);
      }
    } else {
      // Dual view mode navigation
      let prevLeft;
      if (currentPage === 2) {
        prevLeft = 1;
      } else if (currentPage > 2) {
        prevLeft = currentPage - 2;
      }

      if (prevLeft) {
        setFlipDirection('prev');
        setFlipActive(true);
        playFlipSound();

        // Preload prev pages
        fetchPageImage(prevLeft);
        if (prevLeft > 1) fetchPageImage(prevLeft - 1);

        setTimeout(() => {
          setCurrentPage(prevLeft);
          setFlipActive(false);
          setFlipDirection(null);
        }, 500);
      }
    }
  };

  const jumpToPage = (pageNum) => {
    let target = parseInt(pageNum, 10);
    if (isNaN(target) || target < 1 || target > numPages) return;

    if (viewMode === 'dual') {
      if (target === 1) {
        setCurrentPage(1);
      } else {
        // Round target page to the left even page
        setCurrentPage(target % 2 === 0 ? target : target - 1);
      }
    } else {
      setCurrentPage(target);
    }
    playFlipSound();
  };

  // Perform full-text search inside the PDF document
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!pdf || !searchQuery.trim()) return;

    setIsSearching(true);
    setSearchResults([]);
    const results = [];

    try {
      for (let p = 1; p <= numPages; p++) {
        const page = await pdf.getPage(p);
        const textContent = await page.getTextContent();
        const textItems = textContent.items.map(item => item.str).join(' ');

        const index = textItems.toLowerCase().indexOf(searchQuery.toLowerCase());
        if (index !== -1) {
          // Extract context snippet
          const start = Math.max(0, index - 30);
          const end = Math.min(textItems.length, index + searchQuery.length + 40);
          const snippet = (start > 0 ? '...' : '') + textItems.slice(start, end).trim() + (end < textItems.length ? '...' : '');

          results.push({
            pageNum: p,
            snippet: snippet,
          });
        }
      }
      setSearchResults(results);
      if (results.length === 0) {
        message.info('No matches found for your search query.');
      } else {
        message.success(`Found ${results.length} matches!`);
      }
    } catch (err) {
      console.error('Search error:', err);
      message.error('An error occurred during text searching.');
    } finally {
      setIsSearching(false);
    }
  };

  // Toggle browser fullscreen mode
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Keyboard navigation listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pdf, currentPage, viewMode, flipActive, isLoading]);

  // Page image cell components (shows spinner while rendering)
  const PageImage = ({ pageNum }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [coords, setCoords] = useState({ px: 0, py: 0, width: 0, height: 0 });
    const wrapperRef = useRef(null);

    useEffect(() => {
      if (pageNum && !pageImages[pageNum]) {
        fetchPageImage(pageNum);
      }
    }, [pageNum]);

    if (!pageNum) return <div className="page-empty"></div>;

    const imageSrc = pageImages[pageNum];

    const handleMouseMove = (e) => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const px = (e.clientX - rect.left) / scale;
      const py = (e.clientY - rect.top) / scale;
      setCoords({ px, py, width: rect.width / scale, height: rect.height / scale });
    };

    const zoomLevel = 1.4;       // 1.4x magnification level
    const magnifierHeight = 120; // height to show ~5 lines of text

    // Set width to match the page width
    const magnifierWidth = coords.width;
    const leftPosition = 0;

    // Clamp vertical position of the magnifier so it stays within page boundaries
    let topPosition = coords.py - magnifierHeight / 2;
    if (topPosition < 0) {
      topPosition = 0;
    } else if (topPosition > coords.height - magnifierHeight) {
      topPosition = coords.height - magnifierHeight;
    }

    // Calculate background offsets to align the zoomed image proportionally (no stretching)
    const backgroundWidth = coords.width * zoomLevel;
    const backgroundHeight = coords.height * zoomLevel;
    const bgOffsetX = (coords.px - leftPosition) - (coords.px * zoomLevel);
    const bgOffsetY = (coords.py - topPosition) - (coords.py * zoomLevel);

    return (
      <div
        className="page-img-wrapper"
        ref={wrapperRef}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        {imageSrc ? (
          <>
            <img src={imageSrc} alt={`Page ${pageNum}`} className="page-render-img" style={{ display: 'block', width: '100%', height: 'auto' }} />

            {/* Transparent hover detector that captures all events without flickering */}
            <div
              className="magnifier-detector"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onMouseMove={handleMouseMove}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 10,
                cursor: isMagnifierEnabled ? 'crosshair' : 'default',
                backgroundColor: 'transparent'
              }}
            />

            {isMagnifierEnabled && isHovered && coords.width > 0 && (
              <div
                className="page-magnifier-glass"
                style={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  left: `${leftPosition}px`,
                  top: `${topPosition}px`,
                  width: `${magnifierWidth}px`,
                  height: `${magnifierHeight}px`,
                  boxSizing: 'border-box',
                  borderRadius: '8px',
                  border: '3px solid #673bf9',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  backgroundImage: `url(${imageSrc})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: `${backgroundWidth}px ${backgroundHeight}px`,
                  backgroundPosition: `${bgOffsetX}px ${bgOffsetY}px`,
                  zIndex: 5
                }}
              />
            )}
          </>
        ) : (
          <div className="page-render-loading">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
            <span>Loading Page {pageNum}...</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="book-reader-container" ref={containerRef}>

      {/* ─── Control Bar ─── */}
      <div className="reader-toolbar">
        <div className="toolbar-left">
          <Tooltip title={sidebarOpen ? "Close Sidebar" : "Open Book Navigation"}>
            <Button
              type="text"
              icon={<BookOutlined />}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="toolbar-btn"
            />
          </Tooltip>
          <span className="book-title-label">{bookTitle}</span>
        </div>

        <div className="toolbar-center">
          <Button
            disabled={currentPage <= 1 || isLoading}
            onClick={handlePrev}
            icon={<LeftOutlined />}
            className="nav-btn"
          />

          <div className="page-indicator">
            <span>Page</span>
            <Input
              value={currentPage}
              onChange={(e) => {
                const target = parseInt(e.target.value, 10);
                if (!isNaN(target)) jumpToPage(target);
              }}
              className="page-input"
            />
            <span>of {numPages}</span>
          </div>

          <Button
            disabled={
              viewMode === 'single'
                ? currentPage >= numPages
                : (currentPage === 1 ? 2 : currentPage + 2) > numPages
            }
            onClick={handleNext}
            icon={<RightOutlined />}
            className="nav-btn"
          />
        </div>

        <div className="toolbar-right">
          {/* Zoom controls */}
          <Tooltip title="Zoom Out">
            <Button
              type="text"
              icon={<ZoomOutOutlined />}
              onClick={() => setScale(prev => Math.max(0.6, prev - 0.2))}
            />
          </Tooltip>
          <span className="zoom-text">{Math.round(scale * 100)}%</span>
          <Tooltip title="Zoom In">
            <Button
              type="text"
              icon={<ZoomInOutlined />}
              onClick={() => setScale(prev => Math.min(2.5, prev + 0.2))}
            />
          </Tooltip>

          {/* Magnifier glass toggle */}
          <Tooltip title={isMagnifierEnabled ? "Disable Magnifier" : "Enable Magnifier"}>
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={() => setIsMagnifierEnabled(!isMagnifierEnabled)}
              style={isMagnifierEnabled ? { color: '#673bf9', fontWeight: 'bold' } : {}}
            />
          </Tooltip>

          {/* Sound, View Mode, Fullscreen */}
          <Tooltip title={isMuted ? "Unmute paper sound" : "Mute paper sound"}>
            <Button
              type="text"
              icon={isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={() => setIsMuted(!isMuted)}
            />
          </Tooltip>

          <Tooltip title={viewMode === 'dual' ? "Single page view" : "Double page spread"}>
            <Button
              type="text"
              className="hide-mobile-btn"
              onClick={() => setViewMode(prev => prev === 'dual' ? 'single' : 'dual')}
            >
              {viewMode === 'dual' ? '📖 1 Page' : '📖 2 Pages'}
            </Button>
          </Tooltip>

          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen Mode"}>
            <Button
              type="text"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
          </Tooltip>
        </div>
      </div>

      <div className="reader-workspace">

        {/* ─── Sidebar Navigation ─── */}
        <div className={`reader-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="tabs">
              <button
                className={`tab-btn ${sidebarTab === 'thumbnails' ? 'active' : ''}`}
                onClick={() => setSidebarTab('thumbnails')}
              >
                Thumbnails
              </button>
              <button
                className={`tab-btn ${sidebarTab === 'search' ? 'active' : ''}`}
                onClick={() => setSidebarTab('search')}
              >
                Search
              </button>
            </div>
            <Button type="text" icon={<CloseOutlined />} onClick={() => setSidebarOpen(false)} />
          </div>

          <div className="sidebar-body">
            {sidebarTab === 'thumbnails' ? (
              <div className="thumbnails-grid">
                {Array.from({ length: numPages }).map((_, index) => {
                  const pNum = index + 1;
                  return (
                    <div
                      key={pNum}
                      className={`thumbnail-card ${(viewMode === 'dual'
                        ? (currentPage === 1 ? currentPage === pNum : pNum === currentPage || pNum === currentPage + 1)
                        : currentPage === pNum)
                        ? 'active' : ''
                        }`}
                      onClick={() => jumpToPage(pNum)}
                    >
                      <div className="thumbnail-preview">
                        <PageImage pageNum={pNum} />
                      </div>
                      <span className="thumbnail-number">{pNum}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="search-panel">
                <form onSubmit={handleSearch} className="search-form">
                  <Input
                    placeholder="Search query..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    prefix={<SearchOutlined />}
                    className="search-input"
                  />
                  <Button type="primary" htmlType="submit" loading={isSearching}>Search</Button>
                </form>

                <div className="search-results-list">
                  {isSearching ? (
                    <div className="search-status">
                      <Spin size="small" />
                      <span>Scanning pages...</span>
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((res, i) => (
                      <div
                        key={i}
                        className="search-result-item"
                        onClick={() => {
                          jumpToPage(res.pageNum);
                          setSidebarOpen(false);
                        }}
                      >
                        <div className="result-header">
                          <span className="page-badge">Page {res.pageNum}</span>
                        </div>
                        <p className="result-snippet">{res.snippet}</p>
                      </div>
                    ))
                  ) : (
                    <div className="search-empty">
                      Enter a query to search full text inside the book.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── 3D Book Layout Area ─── */}
        <div className="book-stage">
          {isLoading ? (
            <div className="reader-main-loader">
              <Spin size="large" tip="Downloading book PDF document..." />
            </div>
          ) : (
            <div
              className={`book-canvas-wrapper ${flipActive ? 'flipping' : ''}`}
              style={{ transform: `scale(${scale})` }}
            >

              {/* Single page mode layout */}
              {viewMode === 'single' ? (
                <div className={`book-single-page-sheet ${flipActive ? `flip-${flipDirection}` : ''}`}>
                  <PageImage pageNum={currentPage} />
                  <div className="single-page-shadow"></div>
                </div>
              ) : (
                /* Dual page spread layout */
                <div className="book-spread-surface">

                  {/* Left Static Side */}
                  <div className="spread-side left-side">
                    {visiblePages.left ? (
                      <PageImage pageNum={visiblePages.left} />
                    ) : (
                      <div className="page-cover-inside"></div> // Empty area
                    )}
                    <div className="page-fold-gradient left-fold"></div>
                  </div>

                  {/* Right Static Side */}
                  <div className="spread-side right-side">
                    {visiblePages.right ? (
                      <PageImage pageNum={visiblePages.right} />
                    ) : (
                      <div className="page-cover-inside"></div>
                    )}
                    <div className="page-fold-gradient right-fold"></div>
                  </div>

                  {/* Center binding crease seam shadow */}
                  <div className="book-seam-crease"></div>

                  {/* 3D Flip Sheet Card Overlay */}
                  {flipActive && (
                    <div className={`book-flip-card-sheet flip-${flipDirection}`}>

                      {/* Front face of flipping card */}
                      <div className="flip-sheet-face face-front">
                        <PageImage
                          pageNum={
                            flipDirection === 'next'
                              ? (currentPage === 1 ? 1 : currentPage + 1)
                              : currentPage
                          }
                        />
                        <div className="flip-shadow-overlay"></div>
                      </div>

                      {/* Back face of flipping card */}
                      <div className="flip-sheet-face face-back">
                        <PageImage
                          pageNum={
                            flipDirection === 'next'
                              ? (currentPage === 1 ? 2 : currentPage + 2)
                              : (currentPage === 2 ? 1 : currentPage - 1)
                          }
                        />
                        <div className="flip-shadow-overlay"></div>
                      </div>
                    </div>
                  )}

                  {/* Outer binding book borders and paper stack thickness edge */}
                  <div className="book-cover-backborder"></div>
                  <div className="book-page-edges left-edges"></div>
                  <div className="book-page-edges right-edges"></div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookReader;
